(() => {
  const componentId = 'secondary-header-component';
  const defineTimeoutMs = 3000;
  const initTimeoutMs = 5000;

  /**
   * @typedef {HTMLElement & {
   *   swiper?: { initialized?: boolean },
   *   initialize?: () => void
   * }} SwiperContainerElement
   */

  /**
   * @param {Document | Element} [scope=document]
   */
  const initializeSecondaryHeader = async (scope = document) => {
    /** @type {HTMLElement | null} */
    const $component = (scope instanceof Document ? scope.getElementById(componentId) : null)
      || scope.querySelector?.(`#${componentId}`)
      || document.getElementById(componentId);

    if (!$component || $component.dataset.initialized === 'true') return;

    /** @type {SwiperContainerElement | null} */
    const $swiper = $component.querySelector('swiper-container');
    if (!$swiper) return;

    $component.dataset.initialized = 'true';
    $swiper.setAttribute('aria-hidden', 'true');

    let isRevealed = false;
    /** @type {number | null} */
    let revealTimeoutId = null;

    const reveal = () => {
      if (isRevealed) return;
      isRevealed = true;

      if (revealTimeoutId) {
        window.clearTimeout(revealTimeoutId);
      }

      $swiper.removeAttribute('aria-hidden');
      $component.classList.add('secondary-header-component--ready');
    };

    const waitForDefinition = async () => {
      if (customElements.get('swiper-container') != null) return true;

      const result = await Promise.race([
        customElements.whenDefined('swiper-container').then(() => true),
        new Promise((resolve) => {
          window.setTimeout(() => resolve(false), defineTimeoutMs);
        }),
      ]);

      return Boolean(result);
    };

    const isDefined = await waitForDefinition();
    if (!isDefined) {
      reveal();
      return;
    }

    // Swiper Element v11+ prefixes DOM events by default (e.g. `init` -> `swiperinit`).
    $swiper.addEventListener('swiperinit', reveal, { once: true });
    // Keep backward compatibility if eventsPrefix is explicitly disabled.
    $swiper.addEventListener('init', reveal, { once: true });

    if (!$swiper.swiper && typeof $swiper.initialize === 'function') {
      $swiper.initialize();
    }

    if ($swiper.swiper?.initialized) {
      reveal();
    } else {
      revealTimeoutId = window.setTimeout(reveal, initTimeoutMs);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeSecondaryHeader());
  } else {
    initializeSecondaryHeader();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (!(event.target instanceof Element) && !(event.target instanceof Document)) return;
    initializeSecondaryHeader(event.target);
  });
})();
